import React, { useState, useEffect, useCallback } from 'react';
import { Download, Loader2, Search, ClipboardList } from 'lucide-react';
import { purchaseAPI, vendorAPI, branchAPI } from '../../services/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const statusColors = {
  'Draft': 'bg-gray-100 text-gray-600',
  'Ordered': 'bg-amber-100 text-amber-700',
  'In Transit': 'bg-blue-100 text-blue-700',
  'Partially Received': 'bg-purple-100 text-purple-700',
  'Received': 'bg-green-100 text-green-700',
  'Cancelled': 'bg-red-100 text-red-700',
};
const STATUSES = ['Draft', 'Ordered', 'In Transit', 'Partially Received', 'Received', 'Cancelled'];

const inr = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const PoStatusReport = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [branch, setBranch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [poType, setPoType] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    vendorAPI.getAll().then(setVendors).catch(() => {});
    branchAPI.getAll().then(setBranches).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (vendorId) params.vendor_id = vendorId;
      if (branch) params.branch = branch;
      if (statusFilter) params.status_filter = statusFilter;
      if (poType !== 'all') params.po_type = poType;
      if (search.trim()) params.search = search.trim();
      const res = await purchaseAPI.getStatusReport(params);
      setRows(res.rows || []);
      setSummary(res.status_summary || []);
      setTotals(res.totals || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load PO status report');
      setRows([]); setSummary([]); setTotals({});
    } finally { setLoading(false); }
  }, [fromDate, toDate, vendorId, branch, statusFilter, poType, search]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendorId, branch, statusFilter, poType]);

  const exportExcel = () => {
    if (!rows.length) { toast.warning('Nothing to export'); return; }
    const aoa = [['PO Number', 'Title', 'Date', 'Vendor', 'Type', 'Branch', 'Amount (INR)', 'Expected Delivery', 'Status', 'Overdue']];
    rows.forEach(r => aoa.push([
      r.po_number, r.document_title, r.date, r.vendor_name, r.type, r.branch_name,
      r.inr_value, r.expected_delivery, r.status, r.is_overdue ? 'Yes' : 'No',
    ]));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'PO Status');
    XLSX.writeFile(wb, `po-status-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Excel exported');
  };

  return (
    <div data-testid="po-status-report">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Purchase Order Status Report</h2>
          <p className="text-xs text-gray-500">All purchase orders with their current status</p>
        </div>
        <button onClick={exportExcel} disabled={!rows.length || loading} data-testid="po-status-export"
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-40" style={{ backgroundColor: '#24549c' }}>
          <Download className="w-3.5 h-3.5" /> Export Excel
        </button>
      </div>

      {/* Status summary cards (clickable to filter) */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setStatusFilter('')} data-testid="po-status-card-all"
          className={`px-4 py-2.5 rounded-xl border text-left ${!statusFilter ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-white'}`}>
          <p className="text-[10px] text-gray-500 font-semibold uppercase">All POs</p>
          <p className="text-base font-bold text-gray-900">{totals.total_pos || 0} · {inr(totals.total_value)}</p>
        </button>
        {STATUSES.map(s => {
          const row = summary.find(x => x.status === s);
          return (
            <button key={s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)} data-testid={`po-status-card-${s.toLowerCase().replace(/ /g, '-')}`}
              className={`px-4 py-2.5 rounded-xl border text-left ${statusFilter === s ? 'border-blue-400 bg-blue-50' : 'border-gray-100 bg-white'}`}>
              <p className="text-[10px] text-gray-500 font-semibold uppercase">{s}</p>
              <p className="text-base font-bold text-gray-900">{row?.count || 0}</p>
            </button>
          );
        })}
        {totals.overdue > 0 && (
          <div className="px-4 py-2.5 rounded-xl border border-red-100 bg-red-50" data-testid="po-status-overdue">
            <p className="text-[10px] text-red-500 font-semibold uppercase">Overdue</p>
            <p className="text-base font-bold text-red-600">{totals.overdue}</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white p-3 rounded-xl border border-gray-100">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} data-testid="po-status-from"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} data-testid="po-status-to"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Vendor</label>
          <select value={vendorId} onChange={e => setVendorId(e.target.value)} data-testid="po-status-vendor"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg min-w-[150px]">
            <option value="">All Vendors</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Branch</label>
          <select value={branch} onChange={e => setBranch(e.target.value)} data-testid="po-status-branch"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg min-w-[150px]">
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Type</label>
          <select value={poType} onChange={e => setPoType(e.target.value)} data-testid="po-status-type"
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg">
            <option value="all">All</option>
            <option value="domestic">Domestic</option>
            <option value="import">Import</option>
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[10px] font-semibold text-gray-500 mb-1 uppercase">Search</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
              placeholder="PO # / vendor / title…" data-testid="po-status-search"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg" />
          </div>
        </div>
        <button onClick={load} data-testid="po-status-apply"
          className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>Apply</button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="po-status-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#24549c' }} /></div>
      ) : !rows.length ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-sm text-gray-400" data-testid="po-status-empty">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-300" />No purchase orders for the selected filters
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-xs" data-testid="po-status-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['PO Number', 'Date', 'Vendor', 'Type', 'Branch', 'Amount', 'Expected Delivery', 'Status'].map(h =>
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`po-status-row-${i}`}>
                  <td className="px-3 py-2.5 font-mono font-semibold text-gray-900">
                    {r.po_number}
                    {r.revision_number > 0 && <span className="ml-1 text-[9px] text-purple-600 font-bold bg-purple-50 px-1 rounded">Rev-{r.revision_number}</span>}
                    {r.document_title && <div className="text-[10px] text-gray-400 font-sans truncate max-w-[160px]">{r.document_title}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500">{r.date}</td>
                  <td className="px-3 py-2.5 text-gray-700">{r.vendor_name}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 text-[9px] font-semibold rounded-full ${r.type === 'import' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{r.type}</span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-500">{r.branch_name || '—'}</td>
                  <td className="px-3 py-2.5 font-semibold text-gray-900">{r.currency && r.currency !== 'INR' ? `${r.currency} ${r.amount.toLocaleString()}` : inr(r.inr_value)}</td>
                  <td className={`px-3 py-2.5 ${r.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{r.expected_delivery || '—'}{r.is_overdue && <span className="ml-1 text-[9px]">(overdue)</span>}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColors[r.status] || 'bg-gray-100'}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PoStatusReport;
